import React, { useState, useEffect } from "react";
import "../../../style.css";
import { Box, TextField, IconButton, Autocomplete } from "@mui/material";
import { Refresh, Search } from "@mui/icons-material";
import { useProjectApiClient } from "../../Services/api/projectService";

//  Define expected API response types
type SCLeadItem = { sclead_key: number; sclead_name: string };
type RiskItem = { Risk_key: number; Risk_name: string };
type BigBetItem = { bigbet_key: number; bigbetdescription: string };
type statusItems = { projectStatus_key: number; projectStatusvalue: string };
type launchYearsItems = { launchYears_key: number; launchYears_value: string };

type AdvancedFilters = {
  bu: string;
  country: string;
  category: string;
  subCategory: string;
  brand: string;
  scLead: string;
  risk: string;
  bigBet: string;
  projectStatus: string;
  launchYears: string;
  search: string;
};

//  Explicitly define the type for dropdown options
type DropdownOptions = {
  bu: { key: number | string; value: string }[];
  country: { key: number | string; value: string }[];
  category: { key: number | string; value: string }[];
  subCategory: { key: number | string; value: string }[];
  brand: { key: number | string; value: string }[];
  scLead: { key: number | string; value: string }[];
  risk: { key: number | string; value: string }[];
  bigBet: { key: number | string; value: string }[];
  projectStatus: { key: number | string; value: string }[];
  launchYears: { key: number | string; value: string }[];
};

interface AdvanceFiltersProps {
  onFilterChange: (filters: Partial<AdvancedFilters>) => void;
  initialFilters?: Partial<AdvancedFilters>;
}

const AdvancedFilters: React.FC<AdvanceFiltersProps> = ({
  onFilterChange,
  initialFilters,
}) => {
  const {
    getHCGlobalFilters,
    getSclead,
    getRisks,
    getBigbets,
    getProjectStatus,
    getLaunchYears,
  } = useProjectApiClient();

  const [filters, setFilters] = useState<AdvancedFilters>({
    bu: initialFilters?.bu || "",
    country: initialFilters?.country || "",
    category: initialFilters?.category || "",
    subCategory: initialFilters?.subCategory || "",
    brand: initialFilters?.brand || "",
    scLead: initialFilters?.scLead || "",
    risk: initialFilters?.risk || "",
    bigBet: initialFilters?.bigBet || "",
    projectStatus: initialFilters?.projectStatus || "",
    launchYears: initialFilters?.launchYears || "",
    search: initialFilters?.search || "",
  });

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    bu: [],
    country: [],
    category: [],
    subCategory: [],
    brand: [],
    scLead: [],
    risk: [],
    bigBet: [],
    projectStatus: [],
    launchYears: [],
  });

  //  Fetch dependent dropdowns when BU, Country, Category, SubCategory, or Brand changes
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await getHCGlobalFilters({
          buid: filters.bu ? [parseInt(filters.bu)] : [],
          countryKey: filters.country ? [parseInt(filters.country)] : [],
          categoryKey: filters.category ? [parseInt(filters.category)] : [],
          subCategoryKey: filters.subCategory
            ? [parseInt(filters.subCategory)]
            : [],
          brandKey: filters.brand ? [parseInt(filters.brand)] : [],
        });

        //console.log("response.data", response?.data)

        if (response && response.data) {
          setDropdownOptions((prevOptions) => ({
            ...prevOptions,
            bu: convertKeysToString(response.data.bu) || [],
            country: convertKeysToString(response.data.country) || [],
            category: convertKeysToString(response.data.category) || [],
            subCategory: convertKeysToString(response.data.subCategory) || [],
            brand: convertKeysToString(response.data.brand) || [],
          }));
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };

    fetchDropdownOptions();
  }, [
    filters.bu,
    filters.country,
    filters.category,
    filters.subCategory,
    filters.brand,
  ]);

  function convertKeysToString(input: any) {
    return input.map((item: any) => {
      return {
        key: JSON.stringify(item.key),
        value: item.value,
      };
    });
  }

  //  Fetch SC Lead, Risk, and Big Bet once
  useEffect(() => {
    const fetchIndependentOptions = async () => {
      try {
        const [
          scLeadResponse,
          riskResponse,
          bigBetResponse,
          projectStatusResponse,
          launchYearsResponse,
        ] = await Promise.all([
          getSclead(),
          getRisks(),
          getBigbets(),
          getProjectStatus(),
          getLaunchYears(),
        ]);

        setDropdownOptions((prevOptions) => ({
          ...prevOptions,
          scLead:
            scLeadResponse?.map((item: SCLeadItem) => ({
              key: item.sclead_key,
              value: item.sclead_name,
            })) || [],
          risk:
            riskResponse?.map((item: RiskItem) => ({
              key: item.Risk_key,
              value: item.Risk_name,
            })) || [],
          bigBet:
            bigBetResponse?.map((item: BigBetItem) => ({
              key: item.bigbet_key,
              value: item.bigbetdescription,
            })) || [],
          projectStatus:
            projectStatusResponse?.map((item: statusItems) => ({
              key: item.projectStatus_key,
              value: item.projectStatusvalue,
            })) || [],
          launchYears:
            launchYearsResponse?.map((item: launchYearsItems) => ({
              key: item.launchYears_key,
              value: item.launchYears_value,
            })) || [],
        }));
      } catch (error) {
        console.error("Error fetching independent dropdowns:", error);
      }
    };

    fetchIndependentOptions();
  }, []);

  //  Handle dropdown value changes
  const handleFilterChange = (key: keyof AdvancedFilters, value: string) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters); //  Pass updated filters to parent (App.tsx)
  };

  //  Handle search icon click to trigger API
  // const handleSearchClick = () => {
  //   onFilterChange({ ...filters, search: filters.search });
  // };
  const handleSearchClick = () => {
    onFilterChange({ search: filters.search });
    setFilters((prev) => ({ ...prev, search: "" }));
  };
  const handleRefreshClick = () => {
    window.location.reload();
  };
  return (
    <div className="no-hover">
      <Box
        className="filter-container"
        sx={{ display: "flex", alignItems: "left", flexWrap: "wrap", gap: 0.4 }}
      >
        <Autocomplete
          title="BU"
          key="bu"
          options={dropdownOptions.bu || []} // array of { label, value }
          sx={{ width: 150 }}
          value={
            dropdownOptions.bu.find(
              (opt) => String(opt.key) === String(filters.bu)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange("bu", newValue ? newValue.key.toString() : "") // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Select BU" className="country-root-area" />
          )}
        />
        <Autocomplete
          title="Country"
          key="country"
          options={dropdownOptions.country || []} // array of { label, value }
          sx={{ width: 150 }}
          value={
            dropdownOptions.country.find(
              (opt) => String(opt.key) === String(filters.country)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "country",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Country"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />
        <Autocomplete
          title="Category"
          key="category"
          options={dropdownOptions.category || []} // array of { label, value }
          sx={{ width: 140 }}
          value={
            dropdownOptions.category.find(
              (opt) => String(opt.key) === String(filters.category)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "category",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Category"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />
        <Autocomplete
          key="subCategory"
          options={dropdownOptions.subCategory || []} // array of { label, value }
          sx={{ width: 130 }}
          value={
            dropdownOptions.subCategory.find(
              (opt) => String(opt.key) === String(filters.subCategory)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "subCategory",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Brand Position"
              {...params}
              placeholder="Select Brand Position"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />
        <Autocomplete
          key="brand"
          options={dropdownOptions.brand || []} // array of { label, value }
          sx={{ width: 130 }}
          value={
            dropdownOptions.brand.find(
              (opt) => String(opt.key) === String(filters.brand)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "brand",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Brand"
              {...params}
              placeholder="Select Brand"
              variant="outlined"
              className="country-root-area"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.key}>
              {option.value}
            </li>
          )}
        />

        <Autocomplete
          key="scLead"
          options={dropdownOptions.scLead || []} // array of { label, value }
          sx={{ width: 140 }}
          value={
            dropdownOptions.scLead.find(
              (opt) => String(opt.key) === String(filters.scLead)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "scLead",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.value}
          renderInput={(params) => (
            <TextField
              title="SC Lead"
              {...params}
              placeholder="Select SC Lead"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />

        <Autocomplete
          key="risk"
          options={dropdownOptions.risk || []} // array of { label, value }
          sx={{ width: 130 }}
          value={
            dropdownOptions.risk.find(
              (opt) => String(opt.key) === String(filters.risk)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "risk",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Risk"
              {...params}
              placeholder="Select Risk"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />

        <Autocomplete
          key="bigBet"
          options={dropdownOptions.bigBet || []} // array of { label, value }
          sx={{ width: 120 }}
          value={
            dropdownOptions.bigBet.find(
              (opt) => String(opt.key) === String(filters.bigBet)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "bigBet",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Big Bet"
              {...params}
              placeholder="Big Bet"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />

        <Autocomplete
          key="projectStatus"
          options={dropdownOptions.projectStatus || []} // array of { label, value }
          sx={{ width: 120 }}
          value={
            dropdownOptions.projectStatus.find(
              (opt) => String(opt.key) === String(filters.projectStatus)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "projectStatus",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Status"
              {...params}
              placeholder="Status"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />

        <Autocomplete
          key="launchYears"
          options={dropdownOptions.launchYears || []} // array of { label, value }
          sx={{ width: 120 }}
          value={
            dropdownOptions.launchYears.find(
              (opt) => String(opt.key) === String(filters.launchYears)
            ) || null
          }
          onChange={
            (_, newValue) =>
              handleFilterChange(
                "launchYears",
                newValue ? newValue.key.toString() : ""
              ) // store only value
          }
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField
              title="Launch Years"
              {...params}
              placeholder="Launch Years"
              variant="outlined"
              className="country-root-area"
            />
          )}
        />

        <IconButton
          onClick={handleRefreshClick}
          sx={{
            backgroundColor: "#0A53C2", // Blue background
            color: "#fff", // White icon
            borderRadius: "50%", // Ensures a circular shape
            width: "32px", // Explicit width
            height: "32px", // Explicit height
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px", // Adjust padding to avoid square effect
            "&:hover": {
              backgroundColor: "#0849A3", // Darker blue on hover
            },
          }}
        >
          <Refresh />
        </IconButton>

        {/*  Search bar with clickable icon */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            placeholder="Search Projects"
            variant="outlined"
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            sx={{
              borderRadius: "20px",
              backgroundColor: "transperant",
              width: "200px",
              height: "40px",
              padding: "5px",
            }}
          />

          <IconButton
            onClick={handleSearchClick}
            sx={{
              width: "32px", // Explicit width
              height: "32px", // Explicit height
              backgroundColor: "#0A53C2",
              color: "#fff",
              borderRadius: "50%",
            }}
          >
            <Search />
          </IconButton>
        </Box>
      </Box>
    </div>
  );
};

export default AdvancedFilters;
